import React, { createContext, useContext, useState, useEffect } from 'react';

const DevAuthContext = createContext(null);

// Development authentication state storage key
const AUTH_STORAGE_KEY = 'backlinkguard_dev_auth';

// Default test user for development
const TEST_USER = {
  id: '00000000-0000-0000-0000-000000000001',
  firstName: 'Dev',
  lastName: 'User',
  fullName: 'Dev User',
  emailAddresses: [{ emailAddress: 'dev@test.com' }],
  publicMetadata: { plan: 'free' },
};

export function DevAuthProvider({ children }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check for stored auth state
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
    if (storedAuth) {
      try {
        const authData = JSON.parse(storedAuth);
        setIsSignedIn(authData.isSignedIn);
        setUser(authData.isSignedIn ? TEST_USER : null);
      } catch (e) {
        console.error('Error parsing stored auth:', e);
      }
    }
    setIsLoaded(true);
  }, []);

  const signIn = () => {
    setIsSignedIn(true);
    setUser(TEST_USER);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ isSignedIn: true }));
  };

  const signOut = () => {
    setIsSignedIn(false);
    setUser(null);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ isSignedIn: false }));
  };

  return (
    <DevAuthContext.Provider value={{ isLoaded, isSignedIn, user, signIn, signOut }}>
      {children}
    </DevAuthContext.Provider>
  );
}

export function useDevAuth() {
  const context = useContext(DevAuthContext);
  if (!context) {
    throw new Error('useDevAuth must be used within a DevAuthProvider');
  }
  return context;
}

// Components that mimic Clerk's components
export function SignedIn({ children }) {
  const { isLoaded, isSignedIn } = useDevAuth();

  if (!isLoaded) return null;
  if (!isSignedIn) return null;

  return <>{children}</>;
}

export function SignedOut({ children }) {
  const { isLoaded, isSignedIn } = useDevAuth();

  if (!isLoaded) return null;
  if (isSignedIn) return null;

  return <>{children}</>;
}

export function RedirectToSignIn() {
  const { isLoaded } = useDevAuth();

  useEffect(() => {
    if (isLoaded) {
      window.location.href = '/login';
    }
  }, [isLoaded]);

  return null;
}

// Hook to mimic useClerk
export function useClerk() {
  const { signOut } = useDevAuth();
  return { signOut };
}

// Hook to mimic useUser
export function useUser() {
  const { user, isLoaded } = useDevAuth();
  return { user, isLoaded };
}
